/** Place search (SPEC v0.7 §2) — instant local index over OCHA ADM3
 *  (tambon / amphoe / changwat, Thai names). No POI/Nominatim here (local index
 *  is enough for ชาวบ้าน; POI is a documented follow-up). */

export interface SearchEntry {
  name: string; level: 0 | 1 | 2; a: string; p: string; lat: number; lon: number;
  bbox: [number, number, number, number]; // minLon, minLat, maxLon, maxLat
}
type Raw = [string, 0 | 1 | 2, string, string, number, number, number, number, number, number];

const LEVEL_TH: Record<number, string> = { 0: "จังหวัด", 1: "อำเภอ/เขต", 2: "ตำบล/แขวง" };

let entries: SearchEntry[] = [];
let normNames: string[] = [];
let loadP: Promise<void> | null = null;

/** strip administrative prefixes + spaces so "อ.บึงกุ่ม" matches "บึงกุ่ม". */
function norm(s: string): string {
  return (s || "")
    .replace(/จังหวัด|อำเภอ|ตำบล|เขต|แขวง|อ\.|จ\.|ต\.|\s/g, "")
    .toLowerCase();
}

async function load(): Promise<void> {
  if (!loadP) {
    loadP = fetch("/data/search-index.json")
      .then((r) => (r.ok ? r.json() : { entries: [] }))
      .then((d: { entries?: Raw[] }) => {
        entries = (d.entries ?? []).map((e) => ({
          name: e[0], level: e[1], a: e[2], p: e[3], lat: e[4], lon: e[5], bbox: [e[6], e[7], e[8], e[9]],
        }));
        normNames = entries.map((e) => norm(e.name));
      })
      .catch(() => { entries = []; normNames = []; });
  }
  return loadP;
}

/** Ranked matches: exact → prefix → contains, then broader level first. */
export function search(q: string, limit = 20): SearchEntry[] {
  const n = norm(q);
  if (n.length < 1) return [];
  const scored: { e: SearchEntry; s: number }[] = [];
  for (let i = 0; i < entries.length; i++) {
    const nm = normNames[i]!;
    let s: number;
    if (nm === n) s = 0;
    else if (nm.startsWith(n)) s = 1;
    else if (nm.includes(n)) s = 2;
    else continue;
    scored.push({ e: entries[i]!, s });
  }
  scored.sort((a, b) => a.s - b.s || a.e.level - b.e.level || a.e.name.localeCompare(b.e.name, "th"));
  return scored.slice(0, limit).map((x) => x.e);
}

/** "ต.X อ.Y จ.Z" (or แขวง/เขต for Bangkok) describing a result. */
export function entryLabel(e: SearchEntry): string {
  const bkk = e.p === "กรุงเทพมหานคร";
  if (e.level === 0) return `จ.${e.name}`;
  if (e.level === 1) return bkk ? `เขต${e.name} กรุงเทพฯ` : `อ.${e.name} จ.${e.p}`;
  return bkk ? `แขวง${e.name} เขต${e.a} กรุงเทพฯ` : `ต.${e.name} อ.${e.a} จ.${e.p}`;
}

/** Reference-point label for a chosen AREA (not a house coordinate). */
export function areaCenterLabel(e: SearchEntry): string {
  return `📍 จุดอ้างอิง ${entryLabel(e)} (กลางพื้นที่) — ไม่ใช่พิกัดบ้าน`;
}

export function mountSearch(onSelect: (e: SearchEntry) => void): void {
  void load();
  const box = document.createElement("div");
  box.className = "search-box";
  box.innerHTML =
    `<div class="search-row">
       <span class="search-ico" aria-hidden="true">🔍</span>
       <input id="search-input" type="search" autocomplete="off" autocapitalize="off" spellcheck="false"
              placeholder="ค้นหาเขต อำเภอ จังหวัด หรือสถานที่" aria-label="ค้นหาสถานที่" />
       <button id="search-clear" class="search-clear" aria-label="ล้างคำค้น" hidden>✕</button>
     </div>
     <ul id="search-results" class="search-results" role="listbox" hidden></ul>`;
  document.body.appendChild(box);

  const input = box.querySelector<HTMLInputElement>("#search-input")!;
  const clear = box.querySelector<HTMLButtonElement>("#search-clear")!;
  const list = box.querySelector<HTMLUListElement>("#search-results")!;
  let results: SearchEntry[] = [];
  let active = -1;

  const close = (): void => { list.hidden = true; list.replaceChildren(); active = -1; };
  const render = (): void => {
    if (results.length === 0) {
      list.hidden = false;
      list.replaceChildren(Object.assign(document.createElement("li"), { className: "search-empty", textContent: "ไม่พบ — ลองพิมพ์ชื่ออำเภอหรือจังหวัด" }));
      return;
    }
    list.replaceChildren(...results.map((e, i) => {
      const li = document.createElement("li");
      li.className = "search-item" + (i === active ? " active" : "");
      li.setAttribute("role", "option");
      li.innerHTML = `<b>${esc(e.name)}</b> <span class="lvl">${LEVEL_TH[e.level]}</span><span class="sub">${esc(subLabel(e))}</span>`;
      li.addEventListener("mousedown", (ev) => { ev.preventDefault(); choose(i); });
      return li;
    }));
    list.hidden = false;
  };
  const choose = (i: number): void => {
    const e = results[i]; if (!e) return;
    input.value = e.name; close();
    input.blur();
    onSelect(e);
  };

  input.addEventListener("input", () => {
    const q = input.value.trim();
    clear.hidden = q.length === 0;
    if (q.length < 1) { results = []; close(); return; }
    results = search(q);
    active = -1; // never auto-jump to the first result while typing
    render();
  });
  input.addEventListener("keydown", (ev) => {
    if (list.hidden || results.length === 0) { if (ev.key === "Escape") { input.value = ""; clear.hidden = true; close(); } return; }
    if (ev.key === "ArrowDown") { ev.preventDefault(); active = Math.min(results.length - 1, active + 1); render(); }
    else if (ev.key === "ArrowUp") { ev.preventDefault(); active = Math.max(0, active - 1); render(); }
    else if (ev.key === "Enter") { ev.preventDefault(); if (active >= 0) choose(active); }
    else if (ev.key === "Escape") { close(); }
  });
  clear.addEventListener("click", () => { input.value = ""; clear.hidden = true; results = []; close(); input.focus(); });
  document.addEventListener("click", (ev) => { if (!box.contains(ev.target as Node)) close(); });
}

function subLabel(e: SearchEntry): string {
  const bkk = e.p === "กรุงเทพมหานคร";
  if (e.level === 0) return "";
  if (e.level === 1) return bkk ? "กรุงเทพฯ" : `จ.${e.p}`;
  return bkk ? `เขต${e.a} กรุงเทพฯ` : `อ.${e.a} จ.${e.p}`;
}
function esc(s: string): string { return s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c)); }
